/* MÂZIA product card behaviour (snippets/mz-product-card.liquid, sections/mz-quick-add.liquid, and the home
   page's Shop the look rows, sections/mz-home-look.liquid). Loaded once per page as a module by
   layout/theme.liquid. Adding itself is Horizon's: the card's "+" submits Horizon's <product-form-component>
   (assets/product-form.js), so the cart drawer, the cart events and Razorpay behave as everywhere else. What
   follows an add is the bag's (assets/mz-cart.js): desktop opens the bag drawer, phones show the "Added to bag"
   toast and keep the bag shut.

   This file adds:
   1. One tap adds one piece: the card's "+" / Add button, the sheet's Add button and a Shop the look row's Add
      button are aria-busy from the press until the add settles, and a press (tap, click, Enter, Space) on a busy
      button is dropped before Horizon's handlers see it; so is a press on a "+" that is still loading the
      options sheet (assets/quick-add.js).
      A failed add is said out loud: the card's (the sheet's, the row's) [data-mz-add-error] line shows Shopify's
      reason ("… is already sold out", "All 2 … are in your cart"; with the name the card, sheet or row shows in
      place of the full product title: data-mz-title / data-mz-name) or, when the request itself failed (no
      connection, too many requests), the line's data-mz-fallback text; the form's live region announces the
      same words. Horizon writes errors into an addToCartTextError ref only, which these forms leave out (no
      room on a card).
   2. Options sheet: a value whose combination doesn't exist (data-mz-fallback) switches the other options
      to the first available variant that has it, before Horizon's variant picker asks for it (spec 05 §7.5). */

import { StandardEvents } from '@shopify/events';

/* ---------- 1. One tap, one piece; busy while an add runs; a failed add says why ---------- */

const ERROR_MS = 10000;
/** The longest a button stays busy when no answer ever comes (it can be pressed again after) */
const BUSY_MAX_MS = 15000;
/** Buttons that add: the card's "+" / Add (one variant), the sheet's "Add to bag" and a Shop the look row's
    "Add" (one variant; a row with options has a link of the same class instead) */
const ADD_BUTTONS = '[data-mz-card-add], .mz-qa__add, button.mz-home-look__add';
/** Buttons whose press is dropped while they are busy: the adds, and a "+" loading the sheet */
const PRESS_BUTTONS = '.mz-card__add, .mz-qa__add, button.mz-home-look__add';
/** What an add comes from, holding its button and its [data-mz-add-error] line: a card, the sheet's content
    (it lands in the modal without its outer .mz-qa wrapper: .mz-qa__inner stays), a Shop the look row */
const ADD_SOURCES = '.mz-card, .mz-qa__inner, .mz-home-look__row';

/** Shopify's reason for the last failed add, per product form @type {WeakMap<Element, string>} */
const reasons = new WeakMap();
/** @type {WeakMap<Element, number>} */
const timers = new WeakMap();
/** Adds in flight per product form; Horizon's re-renders (morph) can drop aria-busy, this can't @type {WeakMap<Element, number>} */
const inFlight = new WeakMap();
/** @type {WeakMap<Element, number>} */
const busyTimers = new WeakMap();

/** @param {Element} form */
const liveRegion = (form) => form.querySelector('[ref="liveRegion"]');

/** @param {Element} button */
function setBusy(button) {
  button.setAttribute('aria-busy', 'true');
  window.clearTimeout(busyTimers.get(button));
  busyTimers.set(
    button,
    window.setTimeout(() => button.removeAttribute('aria-busy'), BUSY_MAX_MS)
  );
}

/** @param {Element} button */
function clearBusy(button) {
  window.clearTimeout(busyTimers.get(button));
  button.removeAttribute('aria-busy');
}

/**
 * @param {Element} button
 * @returns {boolean}
 */
function isBusy(button) {
  if (button.getAttribute('aria-busy') === 'true') return true;
  const form = button.closest('product-form-component');
  if (!form || !inFlight.get(form)) return false;
  setBusy(button);
  return true;
}

/** @param {Event} event */
function drop(event) {
  event.preventDefault();
  event.stopImmediatePropagation();
}

// Window capture runs before Horizon's document-level on:click / on:submit handlers (assets/component.js).
// Enter and Space on a button arrive as clicks too.
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest(PRESS_BUTTONS) : null;
    if (button && isBusy(button)) drop(event);
  },
  true
);

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const button = form.querySelector(ADD_BUTTONS);
    if (!button) return;
    if (isBusy(button)) {
      drop(event);
      return;
    }
    // Busy from the press: in the sheet, an add pressed during an option change only starts once the change lands
    setBusy(button);
  },
  true
);

/**
 * @param {Element} line - [data-mz-add-error]
 * @param {Element | null} form - the product form whose live region announced it
 */
function hideError(line, form) {
  window.clearTimeout(timers.get(line));
  if (!(line instanceof HTMLElement) || line.hidden) return;
  const live = form && liveRegion(form);
  if (live && live.textContent === line.textContent?.trim()) live.textContent = '';
  line.hidden = true;
}

/**
 * @param {Element} source - .mz-card, .mz-qa__inner or .mz-home-look__row (ADD_SOURCES)
 * @param {Element | null} form
 */
function showError(source, form) {
  const line = source.querySelector('[data-mz-add-error]');
  if (!(line instanceof HTMLElement)) return;
  let text = (form && reasons.get(form)) || line.dataset.mzFallback || '';
  if (!text) return;
  // Name the piece as the card, sheet or row does ("Stella Horse bust"), not with the full product title
  const { mzTitle: title, mzName: name } = line.dataset;
  if (title && name && title !== name) text = text.split(title).join(name);
  const slot = line.querySelector('[data-mz-add-error-text]') || line;
  slot.textContent = text;
  line.hidden = false;
  const live = form && liveRegion(form);
  if (live) live.textContent = text;
  window.clearTimeout(timers.get(line));
  timers.set(
    line,
    window.setTimeout(() => hideError(line, form), ERROR_MS)
  );
}

// The form sends Shopify's reason (CartErrorEvent) before it settles the add's promise.
window.addEventListener(
  StandardEvents.cartError,
  (event) => {
    const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!form) return;
    const error = /** @type {any} */ (event);
    if (error.code !== 'INVALID') return;
    const description = error.detail?.description;
    const text = String((typeof description === 'string' && description) || error.error || '').trim();
    if (text) reasons.set(form, text);
  },
  true
);

window.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const cartEvent = /** @type {any} */ (event);
    if (cartEvent.action !== 'add' || !(event.target instanceof Element)) return;
    const source = event.target.closest(ADD_SOURCES);
    if (!source) return;
    const form = event.target.closest('product-form-component');
    const button = source.querySelector(ADD_BUTTONS);
    const line = source.querySelector('[data-mz-add-error]');
    if (form) reasons.delete(form);
    if (line) hideError(line, form);

    if (form) inFlight.set(form, (inFlight.get(form) || 0) + 1);
    if (button) setBusy(button);
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      const left = form ? (inFlight.get(form) || 1) - 1 : 0;
      if (form) {
        if (left > 0) inFlight.set(form, left);
        else inFlight.delete(form);
      }
      if (left > 0) return;
      if (button) clearBusy(button);
      // A re-render may have swapped the button meanwhile
      form?.querySelectorAll(ADD_BUTTONS).forEach(clearBusy);
    };
    if (!cartEvent.promise) {
      setTimeout(done, 1500);
      return;
    }
    // No answer at all: the button takes presses again after BUSY_MAX_MS
    const safety = window.setTimeout(done, BUSY_MAX_MS);
    cartEvent.promise.then(
      (/** @type {any} */ result) => {
        window.clearTimeout(safety);
        done();
        if (result?.detail?.didError) showError(source, form);
      },
      (/** @type {any} */ error) => {
        window.clearTimeout(safety);
        done();
        if (error?.name !== 'AbortError') showError(source, form);
      }
    );
  },
  true
);

/* ---------- 2. Options sheet: missing combinations ---------- */

document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.mzFallback) return;
    const picker = input.closest('variant-picker');
    if (!picker || !input.closest('.mz-qa__inner')) return;
    for (const id of input.dataset.mzFallback.split(',')) {
      const other = picker.querySelector(`input[data-option-value-id="${CSS.escape(id)}"]`);
      if (other instanceof HTMLInputElement && !other.checked) other.checked = true;
    }
  },
  true
);
